import { BACKEND_URL } from '../utils/config';
import { REFERRALS_ENABLED } from '../utils/featureFlags';
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import EmojiReactions from '../components/campaign/EmojiReactions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Heart,
  Share2,
  MapPin,
  Clock,
  Users,
  BadgeCheck,
  Shield,
  Calendar,
  FileText,
  MessageCircle,
  ChevronRight,
  ChevronDown,
  Facebook,
  Twitter,
  Linkedin,
  Mail,
  Link2,
  CheckCircle2,
  TrendingUp,
  DollarSign,
  Eye,
  Flag,
  Loader2,
  Edit,
  Sparkles,
  Wallet,
  Bell,
  Download,
  History,
  ArrowLeft,
  LayoutDashboard,
  UserPlus,
  X,
  Lock,
  Pause,
  Play,
  Trash2,
  AlertCircle,
  Info,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { featuredCampaigns as mockCampaigns, recentDonations as mockDonations, currencies as allCurrencies } from '../data/mock';
import apiClient, { campaignAPI, donationAPI, paymentAPI } from '../services/api';
import QRPayment from '../components/payment/QRPayment';
import { useAuth } from '../context/AuthContext';
import { getStoredReferralCode } from '../utils/referralTracking';
import { trackCampaignView, trackShare, trackDonation } from '../utils/eventTracking';
import SocialShareModal from '../components/ai/SocialShareModal';
import CampaignOptimizerModal from '../components/ai/CampaignOptimizerModal';
import ThankYouGenerator from '../components/ai/ThankYouGenerator';
import LanguageTranslator from '../components/ai/LanguageTranslator';
import SuccessPredictor from '../components/ai/SuccessPredictor';
import TrustScoreCard, { TrustBadge } from '../components/ai/TrustScoreBadge';
import CampaignComments from '../components/CampaignComments';

// Collaborator Invite Form Component with Autocomplete
const CollaboratorInviteForm = ({ campaignId, onSuccess }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Search users as user types
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }
      
      setSearching(true);
      try {
        const response = await apiClient.get(`/campaigns/users/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(response.data.users || []);
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiClient.post(`/campaigns/${campaignId}/collaborators/invite`, { 
        email: selectedUser.email 
      });
      setSuccess(response.data.message);
      setSelectedUser(null);
      setTimeout(() => onSuccess?.(), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 mt-4">
      {error && <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>}
      {success && <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm">{success}</div>}
      
      {/* Selected User Display */}
      {selectedUser ? (
        <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <img 
            src={selectedUser.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser.name)}&size=40`}
            alt={selectedUser.name}
            className="w-10 h-10 rounded-full"
          />
          <div className="flex-1">
            <p className="font-medium text-foreground">{selectedUser.name}</p>
            <p className="text-xs text-muted-foreground">{selectedUser.email}</p>
          </div>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm"
            onClick={() => setSelectedUser(null)}
            className="text-red-400 hover:bg-red-500/10"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="relative">
          <label className="text-sm text-muted-foreground">Search by name or email</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Start typing to search verified users..."
            className="w-full mt-1 px-3 py-2 bg-secondary border-2 border-emerald-500/40 rounded-lg text-foreground focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition-all"
          />
          {searching && (
            <div className="absolute right-3 top-9">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          )}
          
          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  onClick={() => handleSelectUser(user)}
                  className="flex items-center gap-3 p-3 hover:bg-secondary cursor-pointer border-b border-border last:border-0"
                >
                  <img 
                    src={user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&size=32`}
                    alt={user.name}
                    className="w-8 h-8 rounded-full"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <BadgeCheck className="w-4 h-4 text-emerald-400" />
                </div>
              ))}
            </div>
          )}
          
          {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
            <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg p-3 text-center text-sm text-muted-foreground">
              No verified users found
            </div>
          )}
          
          <p className="text-xs text-muted-foreground mt-1">Only verified users (email + KYC) can be invited</p>
        </div>
      )}
      
      <Button 
        onClick={handleInvite} 
        disabled={loading || !selectedUser} 
        className="w-full bg-emerald-600 hover:bg-emerald-700"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
        Send Invitation to {selectedUser?.name || 'Selected User'}
      </Button>
    </div>
  );
};

const CampaignDetailPage = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Fallback image for when campaign image fails to load
  const fallbackImage = "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800&h=500&fit=crop";
  
  const handleImageError = (e) => {
    e.target.src = fallbackImage;
    e.target.onerror = null; // Prevent infinite loop
  };

  const [campaign, setCampaign] = useState(null);
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // 'pause', 'resume', 'delete'
  const [donationAmount, setDonationAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorMessage, setDonorMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [isSubmittingDonation, setIsSubmittingDonation] = useState(false);
  const [donationError, setDonationError] = useState('');
  const [donationSuccess, setDonationSuccess] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(allCurrencies[0]);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [currencySearch, setCurrencySearch] = useState('');
  const [tipPercentage, setTipPercentage] = useState(15); // Default 15% tip
  const [customTipAmount, setCustomTipAmount] = useState(''); // Custom tip in currency
  const [useCustomTip, setUseCustomTip] = useState(false); // Toggle between % and custom
  const [guestEmail, setGuestEmail] = useState(''); // For guest donations
  const [campaignUpdates, setCampaignUpdates] = useState([]);
  const [showContactCreator, setShowContactCreator] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  
  // Report Campaign state
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);

  // Campaign action handlers
  const handlePauseCampaign = async () => {
    if (!window.confirm('Are you sure you want to pause this campaign? It will stop accepting donations until you resume it.')) {
      return;
    }
    setActionLoading('pause');
    try {
      await apiClient.post(`/campaigns/${id}/pause`);
      setCampaign(prev => ({ ...prev, status: 'inactive' }));
      alert('Campaign paused successfully');
    } catch (err) {
      console.error('Failed to pause campaign:', err);
      alert('Failed to pause campaign. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeCampaign = async () => {
    setActionLoading('resume');
    try {
      await apiClient.post(`/campaigns/${id}/resume`);
      setCampaign(prev => ({ ...prev, status: 'active' }));
    } catch (err) {
      console.error('Failed to resume campaign:', err);
      alert('Failed to resume campaign. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!window.confirm('Are you sure you want to delete this campaign? You can restore it later from your dashboard.')) {
      return;
    }
    setActionLoading('delete');
    try {
      await apiClient.delete(`/campaigns/${id}`);
      alert('Campaign deleted successfully');
      navigate('/dashboard?tab=deleted');
    } catch (err) {
      console.error('Failed to delete campaign:', err);
      alert('Failed to delete campaign. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  // Filter currencies based on search
  const filteredCurrencies = allCurrencies.filter(c =>
    c.code.toLowerCase().includes(currencySearch.toLowerCase()) ||
    c.name.toLowerCase().includes(currencySearch.toLowerCase())
  );
  
  // AI Feature modals
  const [showSocialShare, setShowSocialShare] = useState(false);
  const [showOptimizer, setShowOptimizer] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [autoThankYouEnabled, setAutoThankYouEnabled] = useState(true);
  
  // Referral code generation
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [generatedReferralCode, setGeneratedReferralCode] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  
  // Translation state
  const [translation, setTranslation] = useState(null);
  
  // Campaign Manager modals
  const [showDonationHistory, setShowDonationHistory] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  useEffect(() => {
    const fetchCampaign = async () => {
      setLoading(true);
      try {
        // Fetch campaign data first (required for other calls)
        const data = await campaignAPI.get(id);
        
        // Set campaign immediately so UI renders faster
        setCampaign({
          id: data.id,
          title: data.title,
          story: data.story,
          goal: data.goal,
          raised: data.raised,
          donors: data.donors,
          category: data.category,
          image: data.image,
          video_url: data.video_url,
          creator: data.creator,
          location: data.location,
          daysLeft: data.days_left,
          verified: data.verified,
          escrow: data.escrow,
          trending: data.trending,
          milestones: data.milestones || [],
          updates: data.updates || 0,
          status: data.status || 'active',
          collaborators: data.collaborators || [],
          pending_invites: data.pending_invites || [],
          pending_review_reason: data.pending_review_reason || null,
          rejection_reason: data.rejection_reason || null,
          type: data.type || 'campaign',
          team_fundraising_enabled: data.team_fundraising_enabled || false,
          team_pages_count: data.team_pages_count || 0,
          team_raised_total: data.team_raised_total || 0,
        });
        
        // Set auto thank you setting (default to true if not set)
        setAutoThankYouEnabled(data.auto_thank_you_enabled !== false);
        
        // Stop loading immediately after main data is set
        setLoading(false);

        // Run all secondary fetches in PARALLEL (non-blocking)
        const fetchPromises = [];
        
        // Track page view (fire and forget - don't await)
        fetch(`${BACKEND_URL}/api/analytics/track/view`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            campaign_id: id,
            referrer: document.referrer || null,
            source: new URLSearchParams(window.location.search).get('utm_source') || 'direct'
          })
        }).catch(() => {});

        // Track campaign view for recommendation engine (consent-aware)
        trackCampaignView(id, data.category, token).catch(() => {});

        // Fetch creator profile (parallel)
        if (data.creator?.id) {
          fetchPromises.push(
            apiClient.get(`/profile/${data.creator.id}`)
              .then(res => setCreatorProfile(res.data))
              .catch(() => {})
          );
        }

        // Fetch donations (parallel)
        fetchPromises.push(
          donationAPI.getByCampaign(id)
            .then(donationData => {
              setDonations(donationData.map(d => ({
                id: d.id,
                donor: d.anonymous ? 'Anonymous' : d.donor_name,
                amount: d.amount,
                message: d.message,
                time: new Date(d.created_at).toLocaleDateString(),
              })));
            })
            .catch(() => setDonations(mockDonations))
        );

        // Fetch campaign updates (parallel)
        fetchPromises.push(
          campaignAPI.getUpdates(id)
            .then(updatesRes => setCampaignUpdates(updatesRes))
            .catch(() => {})
        );

        // Wait for all secondary fetches to complete
        await Promise.allSettled(fetchPromises);
        
      } catch (error) {
        console.error('Failed to fetch campaign:', error);
        // Fallback to mock
        const mockCampaign = mockCampaigns.find((c) => c.id === id) || mockCampaigns[0];
        setCampaign(mockCampaign);
        setDonations(mockDonations);
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [id]);

  // Fetch notifications when modal opens
  const fetchNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const response = await apiClient.get('/notifications/', {
        params: { limit: 20 }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      await apiClient.post(`/notifications/mark-read/${notificationId}`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsRead = async () => {
    try {
      await apiClient.post('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Send message to campaign creator
  const handleSendMessageToCreator = async () => {
    if (!contactMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await apiClient.post('/contact/send', {
        recipient_id: campaign?.creator?.id,
        campaign_id: id,
        subject: contactSubject || `Question about: ${campaign?.title}`,
        message: contactMessage,
        sender_name: user?.name || 'Anonymous User',
        sender_email: user?.email || ''
      });
      setMessageSent(true);
      setContactMessage('');
      setContactSubject('');
      setTimeout(() => {
        setShowContactCreator(false);
        setMessageSent(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle Report Campaign
  const handleReportCampaign = async () => {
    if (!reportReason) {
      alert('Please select a reason for reporting');
      return;
    }
    
    setIsSubmittingReport(true);
    try {
      await apiClient.post('/reports/campaign', {
        campaign_id: id,
        campaign_title: campaign?.title,
        reason: reportReason,
        details: reportDetails,
        reporter_id: user?.id || null,
        reporter_email: user?.email || null,
        reporter_name: user?.name || 'Anonymous'
      });
      setReportSubmitted(true);
      setTimeout(() => {
        setShowReportDialog(false);
        setReportSubmitted(false);
        setReportReason('');
        setReportDetails('');
      }, 2000);
    } catch (error) {
      console.error('Failed to submit report:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1">
          {/* Skeleton loader that matches page layout */}
          <div className="bg-gradient-to-br from-emerald-900/20 to-teal-900/20 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="h-8 w-32 bg-muted/50 rounded animate-pulse mb-4"></div>
              <div className="h-10 w-3/4 bg-muted/50 rounded animate-pulse"></div>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="h-[400px] bg-muted/50 rounded-2xl animate-pulse"></div>
                <div className="space-y-3">
                  <div className="h-6 w-full bg-muted/50 rounded animate-pulse"></div>
                  <div className="h-6 w-5/6 bg-muted/50 rounded animate-pulse"></div>
                  <div className="h-6 w-4/6 bg-muted/50 rounded animate-pulse"></div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="h-[300px] bg-muted/50 rounded-2xl animate-pulse"></div>
                <div className="h-[200px] bg-muted/50 rounded-2xl animate-pulse"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">{t('campaignNotFoundMsg')}</p>
        </main>
        <Footer />
      </div>
    );
  }

  const progressPercent = Math.min((campaign.raised / campaign.goal) * 100, 100);

  const formatCurrency = (amount, currencyCode = null) => {
    const currency = currencyCode || campaign?.currency || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const suggestedAmounts = [25, 50, 100, 250, 500, 1000];

  // Calculate tip amount based on donation and percentage or custom amount
  const calculateTipAmount = () => {
    if (useCustomTip) {
      return parseFloat(customTipAmount || 0);
    }
    const baseAmount = parseFloat(donationAmount || customAmount || 0);
    return (baseAmount * tipPercentage) / 100;
  };

  // Calculate total amount (donation + tip)
  const calculateTotalAmount = () => {
    const baseAmount = parseFloat(donationAmount || customAmount || 0);
    const tip = calculateTipAmount();
    return baseAmount + tip;
  };

  // Generate campaign-specific referral code
  const handleGenerateReferralCode = async () => {
    if (!token) {
      alert('Please login to generate a referral code');
      return;
    }
    
    setReferralLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/referrals/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          campaign_id: campaign.id,
          referral_type: 'campaign'
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate code');
      }
      
      const data = await response.json();
      setGeneratedReferralCode(data.referral_code);
      setShowReferralModal(true);
    } catch (error) {
      console.error('Failed to generate referral code:', error);
      alert(error.message || 'Failed to generate referral code');
    } finally {
      setReferralLoading(false);
    }
  };

  const handleDonate = async () => {
    const amount = donationAmount || customAmount;
    if (!amount) return;

    // For guest donations, validate email
    const donorEmail = user?.email || guestEmail;
    if (!donorEmail || !donorEmail.includes('@')) {
      setDonationError('Please enter a valid email address to receive your donation receipt');
      return;
    }

    setIsSubmittingDonation(true);
    setDonationError('');

    const baseAmount = parseFloat(amount);
    const tipAmount = calculateTipAmount();
    const totalAmount = baseAmount + tipAmount;

    try {
      // Get referral code if stored — only when feature is enabled
      const storedReferralCode = REFERRALS_ENABLED ? getStoredReferralCode() : null;
      
      // Create Stripe checkout session with tip included (works for both logged-in and guest users)
      const donationData = {
        campaignId: campaign.id,
        amount: baseAmount,
        tipAmount: tipAmount,
        totalAmount: totalAmount,
        currency: selectedCurrency.code.toLowerCase(),
        donorName: isAnonymous ? 'Anonymous' : (donorName || user?.name || 'Guest Donor'),
        donorEmail: donorEmail,
        donorId: user?.id || null,  // Include user ID for dashboard tracking
        message: donorMessage || '',
        isAnonymous: isAnonymous,
        isGuest: !user, // Flag to indicate guest donation
        referralCode: storedReferralCode || null, // Include referral code for attribution
      };

      const response = await paymentAPI.createDonationSession(donationData);
      
      // Redirect to Stripe Checkout
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        throw new Error('Failed to get checkout URL');
      }
    } catch (error) {
      console.error('Donation failed:', error);
      const detail = error.response?.data?.detail || 'Failed to process donation. Please try again.';
      setDonationError(detail);
      // If the backend rejected the donation because of self-referral, clear
      // the stored referral code so the user can retry without it.
      if (typeof detail === 'string' && detail.toLowerCase().includes('own referral code')) {
        try {
          localStorage.removeItem('referral_code');
          localStorage.removeItem('ftf_referral_code');
        } catch {}
      }
      setIsSubmittingDonation(false);
    }
  };

  const milestones = [
    { id: 1, name: 'Campaign Launched', status: 'completed', date: 'Jan 15, 2024' },
    { id: 2, name: 'Initial Treatment Started', status: 'completed', date: 'Feb 1, 2024' },
    { id: 3, name: campaign.milestone?.name || 'Current Phase', status: 'current', date: 'In Progress' },
    { id: 4, name: 'Final Treatment', status: 'pending', date: 'Upcoming' },
  ];

  const fundUsage = [
    { category: 'Medical Treatment', amount: 85000, percent: 66 },
    { category: 'Medication', amount: 25000, percent: 20 },
    { category: 'Hospital Stay', amount: 12000, percent: 9 },
    { category: 'Travel & Accommodation', amount: 5850, percent: 5 },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      {/* Donation Success Banner */}
      {donationSuccess && (
        <div className="fixed top-4 right-4 z-50 bg-emerald-500 text-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 animate-pulse">
          <CheckCircle2 className="w-6 h-6" />
          <div>
            <p className="font-semibold">{t('thankYouForDonation')}</p>
            <p className="text-sm opacity-90">{t('contributionMakesDifference')}</p>
          </div>
          <button onClick={() => setDonationSuccess(false)} className="ml-4 text-white/80 hover:text-white">
            ✕
          </button>
        </div>
      )}
      
      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm text-muted-foreground">
                <Link to="/" className="hover:text-emerald-400">{t('home')}</Link>
                <ChevronRight className="w-4 h-4 mx-2" />
                {/* If user owns this campaign, link to dashboard, otherwise to discover */}
                {user && campaign.creator?.id === user?.id ? (
                  <Link to="/dashboard" className="hover:text-emerald-400">{t('myCampaigns')}</Link>
                ) : (
                  <Link to="/discover" className="hover:text-emerald-400">{t('campaignsLabel')}</Link>
                )}
                <ChevronRight className="w-4 h-4 mx-2" />
                <span className="text-foreground capitalize">{campaign.category}</span>
              </div>
              
              {/* Back to My Campaigns button for campaign owner */}
              {user && campaign.creator?.id === user?.id && (
                <Link to="/dashboard">
                  <Button variant="outline" size="sm" className="gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    {t('backToDashboard')}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Campaign Image or Video */}
              <div className="relative">
                <div className="rounded-2xl overflow-hidden">
                  {campaign.video_url ? (
                    // Display video if available
                    <div className="relative w-full h-[400px] bg-black">
                      {campaign.video_url.includes('youtube.com') || campaign.video_url.includes('youtu.be') ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${
                            campaign.video_url.includes('youtu.be') 
                              ? campaign.video_url.split('youtu.be/')[1]?.split('?')[0]
                              : campaign.video_url.includes('v=')
                                ? campaign.video_url.split('v=')[1]?.split('&')[0]
                                : campaign.video_url.split('embed/')[1]?.split('?')[0]
                          }`}
                          className="absolute inset-0 w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={campaign.title}
                        />
                      ) : (
                        <video
                          src={campaign.video_url}
                          controls
                          className="w-full h-full object-contain"
                          poster={campaign.image || fallbackImage}
                        >
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                  ) : (
                    <img
                      src={campaign.image || fallbackImage}
                      alt={campaign.title}
                      loading="eager"
                      fetchPriority="high"
                      onError={handleImageError}
                      className="w-full h-[400px] object-cover"
                    />
                  )}
                </div>
                {/* Badges - Top Left */}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  {campaign.trending && (
                    <Badge className="bg-orange-500 text-white border-none">
                      <TrendingUp className="w-3 h-3 mr-1" /> Trending
                    </Badge>
                  )}
                  {campaign.verified && (
                    <Badge className="bg-emerald-500 text-white border-none">
                      <BadgeCheck className="w-3 h-3 mr-1" /> Verified
                    </Badge>
                  )}
                  {campaign.escrow && (
                    <Badge className="bg-blue-500 text-white border-none">
                      <Shield className="w-3 h-3 mr-1" /> Escrow Protected
                    </Badge>
                  )}
                </div>
                
                {/* Emoji Reactions - Bottom Right */}
                <div className="absolute bottom-4 right-4 z-50">
                  <EmojiReactions campaignId={id} />
                </div>
              </div>

              {/* Campaign Title & Creator */}
              <div>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-3xl font-bold text-foreground break-words">
                      {translation ? translation.translated_title : campaign.title}
                    </h1>
                    {/* Campaign Status Badge with Tooltip */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help">
                            {campaign.status === 'active' && (
                              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Active
                              </Badge>
                            )}
                            {campaign.status === 'inactive' && (
                              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 gap-1">
                                <Pause className="w-3 h-3" /> Paused
                              </Badge>
                            )}
                            {campaign.status === 'pending_verification' && (
                              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 gap-1">
                                <Clock className="w-3 h-3" /> Pending
                              </Badge>
                            )}
                            {campaign.status === 'draft' && (
                              <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30 gap-1">
                                <Edit className="w-3 h-3" /> Draft
                              </Badge>
                            )}
                            {campaign.status === 'rejected' && (
                              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 gap-1">
                                <AlertCircle className="w-3 h-3" /> Rejected
                              </Badge>
                            )}
                            {campaign.status === 'completed' && (
                              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Completed
                              </Badge>
                            )}
                            {campaign.status === 'deleted' && (
                              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 gap-1">
                                <Trash2 className="w-3 h-3" /> Deleted
                              </Badge>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs bg-card border border-border text-foreground p-3">
                          {campaign.status === 'active' && (
                            <p>This {campaign.type || 'campaign'} is live and accepting donations.</p>
                          )}
                          {campaign.status === 'inactive' && (
                            <p>This {campaign.type || 'campaign'} is paused and not accepting donations.</p>
                          )}
                          {campaign.status === 'pending_verification' && (
                            <div>
                              <p className="font-medium mb-1">Pending Review</p>
                              <p className="text-muted-foreground text-xs">
                                {campaign.pending_review_reason || 'This campaign is awaiting admin verification before going live.'}
                              </p>
                            </div>
                          )}
                          {campaign.status === 'draft' && (
                            <p>This {campaign.type || 'campaign'} is a draft and not yet published.</p>
                          )}
                          {campaign.status === 'rejected' && (
                            <div>
                              <p className="font-medium mb-1">Rejected</p>
                              <p className="text-muted-foreground text-xs">
                                {campaign.rejection_reason || 'This campaign was rejected by admin review.'}
                              </p>
                            </div>
                          )}
                          {campaign.status === 'completed' && (
                            <p>This {campaign.type || 'campaign'} has been completed.</p>
                          )}
                          {campaign.status === 'deleted' && (
                            <p>This {campaign.type || 'campaign'} has been deleted.</p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <TrustBadge campaignId={id} />
                </div>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <Link to={`/profile/${campaign.creator.id}`} className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
                    <img
                      src={campaign.creator.avatar}
                      alt={campaign.creator.name}
                      className="w-12 h-12 rounded-full border-2 border-emerald-500"
                    />
                    <div>
                      <p className="font-medium text-foreground flex items-center">
                        {campaign.creator.name}
                        {campaign.creator.verified && (
                          <BadgeCheck className="w-4 h-4 ml-1 text-emerald-500" />
                        )}
                      </p>
                      <div className="flex items-center gap-2">
                        {creatorProfile?.member_id && (
                          <Badge className="bg-emerald-500/20 text-emerald-500 text-xs font-mono">
                            ID: {creatorProfile.member_id}
                          </Badge>
                        )}
                        <span className="text-sm text-emerald-500 hover:underline">View Profile</span>
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-3">
                    {/* Message Creator Button */}
                    {user && campaign.creator?.id !== user?.id && campaign.creator?.verified && (
                      <Link to={`/messages?to=${campaign.creator.id}`}>
                        <Button 
                          variant="outline" 
                          className="border-emerald-500 text-emerald-500 hover:bg-emerald-500 hover:text-white"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Message Creator
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>

                {/* Collaborators Display */}
                {campaign.collaborators && campaign.collaborators.length > 0 && (
                  <div className="flex flex-col gap-3 mt-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-emerald-400 font-medium">Trusted Collaborators:</span>
                    </div>
                    <div className="flex flex-col gap-2">
                      {campaign.collaborators.map((collab) => (
                        <div key={collab.id} className="flex items-center justify-between gap-3 px-3 py-2 bg-emerald-500/20 rounded-lg" data-testid={`collaborator-row-${collab.id}`}>
                          <div className="flex items-center gap-2">
                            <img src={collab.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(collab.name)}&size=24`} alt={collab.name} className="w-7 h-7 rounded-full" />
                            <span className="text-sm text-emerald-300 font-medium">{collab.name}</span>
                            <BadgeCheck className="w-4 h-4 text-emerald-400" />
                          </div>
                          {/* Remove button - only show to owner */}
                          {user && campaign.creator?.id === user?.id && (
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                if (!window.confirm(`Remove ${collab.name} as collaborator?`)) return;
                                try {
                                  await apiClient.delete(`/campaigns/${id}/collaborators/${collab.id}`);
                                  window.location.reload();
                                } catch (err) {
                                  alert('Failed to remove collaborator: ' + (err.response?.data?.detail || 'Unknown error'));
                                }
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-500/20 text-red-400 hover:bg-red-500/40 border border-red-500/30 text-xs font-medium transition-colors"
                              title={`Remove ${collab.name}`}
                              data-testid={`remove-collaborator-${collab.id}`}
                            >
                              <X className="w-3.5 h-3.5" />
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Owner Controls */}
                {user && campaign.creator?.id === user?.id && (
                  <div className="flex flex-wrap gap-3 mt-4 p-4 bg-gradient-to-r from-emerald-500/10 to-teal-600/10 border border-emerald-500/30 rounded-xl">
                    <Link to={`/campaign/${id}/edit`}>
                      <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90">
                        <FileText className="w-4 h-4 mr-2" /> Edit Campaign
                      </Button>
                    </Link>
                    <Link to={`/campaign/${id}/update`}>
                      <Button variant="outline" className="border-emerald-500/50 hover:bg-emerald-500/10">
                        <MessageCircle className="w-4 h-4 mr-2" /> Post Update
                      </Button>
                    </Link>
                    {/* Invite Collaborator - max 2 (including pending invites) */}
                    {((campaign.collaborators?.length || 0) + (campaign.pending_invites?.length || 0) < 2) && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10">
                            <UserPlus className="w-4 h-4 mr-2" /> Invite Collaborator
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Invite Collaborator</DialogTitle>
                            <DialogDescription>
                              Invite a verified user to collaborate on your campaign. This builds trust with donors. (Max 2 collaborators)
                            </DialogDescription>
                          </DialogHeader>
                          <CollaboratorInviteForm campaignId={id} onSuccess={() => window.location.reload()} />
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                )}
              </div>

              {/* Tabs */}
              <Tabs defaultValue="story" className="w-full">
                <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1">
                  <TabsTrigger value="story" className="flex-1 sm:flex-none">
                    <FileText className="w-4 h-4 mr-2" /> Story
                  </TabsTrigger>
                  <TabsTrigger value="updates" className="flex-1 sm:flex-none">
                    <MessageCircle className="w-4 h-4 mr-2" /> Updates ({campaign.updates})
                  </TabsTrigger>
                  <TabsTrigger value="donors" className="flex-1 sm:flex-none">
                    <Users className="w-4 h-4 mr-2" /> Donors
                  </TabsTrigger>
                  <TabsTrigger value="milestones" className="flex-1 sm:flex-none">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Milestones
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="story" className="mt-6">
                  {/* Language Translator */}
                  <div className="mb-4">
                    <LanguageTranslator
                      campaignId={id}
                      originalTitle={campaign.title}
                      originalStory={campaign.story}
                      onTranslate={setTranslation}
                    />
                  </div>
                  
                  <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
                    <div className="prose max-w-none">
                      <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                        {translation ? translation.translated_story : campaign.story}
                      </p>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="updates" className="mt-6">
                  <div className="space-y-4">
                    {campaignUpdates.length > 0 ? (
                      campaignUpdates.map((update, i) => (
                        <div key={update.id} className="bg-card border border-border rounded-xl p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">
                                {new Date(update.created_at).toLocaleDateString('en-US', { 
                                  month: 'long', day: 'numeric', year: 'numeric' 
                                })}
                              </span>
                              <Badge variant="outline" className="border-emerald-500/50 text-emerald-400">Update #{campaignUpdates.length - i}</Badge>
                            </div>
                            <span className="text-sm text-muted-foreground">by {update.author_name}</span>
                          </div>
                          <h4 className="font-semibold text-foreground mb-2 text-lg">{update.title}</h4>
                          <p className="text-muted-foreground whitespace-pre-wrap">{update.content}</p>
                          {update.image && (
                            <img 
                              src={update.image} 
                              alt="Update" 
                              onError={handleImageError}
                              className="mt-4 rounded-lg w-full max-h-64 object-cover"
                            />
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12 bg-card border border-border rounded-xl">
                        <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No updates posted yet</p>
                        {user && campaign?.creator?.id === user?.id && (
                          <Link to={`/campaign/${id}/update`}>
                            <Button className="mt-4 bg-gradient-to-r from-emerald-500 to-teal-600">
                              Post First Update
                            </Button>
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="donors" className="mt-6">
                  <div className="bg-card rounded-xl p-6 border border-border">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="font-semibold text-foreground">{campaign.donors.toLocaleString()} Donors</h3>
                      <Badge variant="secondary">Recent</Badge>
                    </div>
                    <div className="space-y-4">
                      {donations.length > 0 ? donations.slice(0, 10).map((donation) => (
                        <div key={donation.id} className="flex items-start space-x-4 pb-4 border-b border-border last:border-0">
                          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                            <Heart className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-foreground">{donation.donor}</p>
                              <span className="font-semibold text-emerald-400">{formatCurrency(donation.amount)}</span>
                            </div>
                            <p className="text-sm text-muted-foreground">{donation.time}</p>
                            {donation.message && (
                              <p className="text-sm text-muted-foreground mt-1 italic">"{donation.message}"</p>
                            )}
                          </div>
                        </div>
                      )) : (
                        <p className="text-center text-muted-foreground py-4">Be the first to donate!</p>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="milestones" className="mt-6">
                  <div className="bg-card rounded-xl p-6 border border-border">
                    <h3 className="font-semibold text-foreground mb-6">Funding Milestones</h3>
                    <div className="space-y-4">
                      {milestones.map((milestone, index) => (
                        <div key={milestone.id} className="flex items-start space-x-4">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            milestone.status === 'completed' ? 'bg-emerald-500/20' :
                            milestone.status === 'current' ? 'bg-teal-500/20' : 'bg-secondary'
                          }`}>
                            {milestone.status === 'completed' ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            ) : milestone.status === 'current' ? (
                              <Clock className="w-5 h-5 text-teal-400" />
                            ) : (
                              <span className="w-2 h-2 rounded-full bg-muted-foreground" />
                            )}
                          </div>
                          <div className="flex-1 pb-4 border-b border-border last:border-0">
                            <div className="flex items-center justify-between">
                              <p className={`font-medium ${
                                milestone.status === 'completed' ? 'text-foreground' :
                                milestone.status === 'current' ? 'text-teal-400' : 'text-muted-foreground'
                              }`}>{milestone.name}</p>
                              <Badge variant={milestone.status === 'completed' ? 'default' : 'secondary'} className="capitalize">
                                {milestone.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-500">{milestone.date}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
              
              {/* Comments Section */}
              <div className="mt-8">
                <CampaignComments 
                  campaignId={campaign.id} 
                  creatorId={campaign.creator?.id || campaign.creator_id}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Donation Card */}
                <div className="bg-card rounded-2xl shadow-lg p-6 border border-border">
                  {/* Progress */}
                  <div className="mb-6">
                    <div className="flex justify-between items-end mb-2">
                      <div>
                        <p className="text-3xl font-bold text-emerald-400">
                          {formatCurrency(campaign.raised)}
                        </p>
                        <p className="text-muted-foreground">
                          raised of {formatCurrency(campaign.goal)} goal
                        </p>
                      </div>
                      <span className="text-lg font-semibold text-foreground">
                        {Math.round(progressPercent)}%
                      </span>
                    </div>
                    <Progress value={progressPercent} className="h-3 bg-secondary" />
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-3 bg-secondary rounded-lg">
                      <p className="text-2xl font-bold text-foreground">{campaign.donors.toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">{t('donors')}</p>
                    </div>
                    <div className="text-center p-3 bg-secondary rounded-lg">
                      <p className="text-2xl font-bold text-foreground">{campaign.daysLeft}</p>
                      <p className="text-sm text-muted-foreground">{t('daysLeft')}</p>
                    </div>
                  </div>

                  {/* Donate Button - Links to full-screen donation page */}
                  <Link to={`/donate/${id}`}>
                    <Button className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white py-6 text-lg rounded-xl mb-3">
                      <Heart className="mr-2 w-5 h-5" /> {t('donateNow')}
                    </Button>
                  </Link>

                  {/* Keep dialog for quick donate option */}
                  <Dialog open={showDonateModal} onOpenChange={(open) => {
                    setShowDonateModal(open);
                    if (!open) {
                      setCurrencySearch('');
                      setShowCurrencyDropdown(false);
                    }
                  }}>
                    <DialogContent className="max-w-2xl bg-card border-border max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-foreground text-xl">{t('makeADonation')}</DialogTitle>
                        <DialogDescription className="text-muted-foreground">
                          {t('supportThisCause')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
                        {/* Left Column - Currency and Amount */}
                        <div className="space-y-4">
                          {/* Currency Selector with Search */}
                          <div className="relative">
                            <label className="block text-sm font-medium text-foreground mb-2">{t('selectCurrencyLabel')}</label>
                            <button
                              type="button"
                              onClick={() => {
                                setShowCurrencyDropdown(!showCurrencyDropdown);
                                setCurrencySearch('');
                              }}
                              className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-secondary border border-border rounded-lg hover:bg-secondary/80 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">{selectedCurrency.flag}</span>
                                <div className="text-left">
                                  <span className="font-medium text-foreground">{selectedCurrency.code}</span>
                                  <span className="text-muted-foreground text-sm ml-1">({selectedCurrency.symbol})</span>
                                </div>
                              </div>
                              <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {showCurrencyDropdown && (
                              <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50">
                                {/* Search Input */}
                                <div className="p-2 border-b border-border sticky top-0 bg-card">
                                  <Input
                                    type="text"
                                    placeholder="Type to search currency..."
                                    value={currencySearch}
                                    onChange={(e) => setCurrencySearch(e.target.value)}
                                    className="h-9"
                                    autoFocus
                                  />
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                  {/* Popular currencies when not searching */}
                                  {currencySearch === '' && (
                                    <div className="px-3 py-1.5 text-xs text-muted-foreground font-medium bg-secondary/50">Popular</div>
                                  )}
                                  {filteredCurrencies.filter(c => currencySearch === '' ? c.popular : true).slice(0, currencySearch ? 50 : 10).map((currency) => (
                                    <button
                                      key={currency.code}
                                      type="button"
                                      onClick={() => {
                                        setSelectedCurrency(currency);
                                        setShowCurrencyDropdown(false);
                                        setCurrencySearch('');
                                      }}
                                      className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-secondary transition-colors text-left ${
                                        selectedCurrency.code === currency.code ? 'bg-emerald-500/20' : ''
                                      }`}
                                    >
                                      <span className="text-lg">{currency.flag}</span>
                                      <span className="font-medium text-sm">{currency.code}</span>
                                      <span className="text-xs text-muted-foreground truncate">{currency.name}</span>
                                      <span className="text-xs text-muted-foreground ml-auto">{currency.symbol}</span>
                                    </button>
                                  ))}
                                  {/* All currencies when not searching */}
                                  {currencySearch === '' && (
                                    <>
                                      <div className="px-3 py-1.5 text-xs text-muted-foreground font-medium bg-secondary/50 border-t border-border">All Currencies</div>
                                      {filteredCurrencies.filter(c => !c.popular).slice(0, 20).map((currency) => (
                                        <button
                                          key={currency.code}
                                          type="button"
                                          onClick={() => {
                                            setSelectedCurrency(currency);
                                            setShowCurrencyDropdown(false);
                                            setCurrencySearch('');
                                          }}
                                          className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-secondary transition-colors text-left ${
                                            selectedCurrency.code === currency.code ? 'bg-emerald-500/20' : ''
                                          }`}
                                        >
                                          <span className="text-lg">{currency.flag}</span>
                                          <span className="font-medium text-sm">{currency.code}</span>
                                          <span className="text-xs text-muted-foreground truncate">{currency.name}</span>
                                          <span className="text-xs text-muted-foreground ml-auto">{currency.symbol}</span>
                                        </button>
                                      ))}
                                      <div className="px-3 py-2 text-xs text-center text-muted-foreground">
                                        Type to search 150+ currencies
                                      </div>
                                    </>
                                  )}
                                  {currencySearch && filteredCurrencies.length === 0 && (
                                    <div className="px-3 py-4 text-sm text-center text-muted-foreground">
                                      No currency found for "{currencySearch}"
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Suggested Amounts */}
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">{t('selectAmount')}</label>
                            <div className="grid grid-cols-3 gap-2">
                              {suggestedAmounts.map((amount) => (
                                <Button
                                  key={amount}
                                  variant={donationAmount === amount.toString() ? 'default' : 'outline'}
                                  onClick={() => {
                                    setDonationAmount(amount.toString());
                                    setCustomAmount('');
                                  }}
                                  className={`py-3 text-sm ${donationAmount === amount.toString() ? 'bg-gradient-to-r from-emerald-500 to-teal-600' : ''}`}
                                >
                                  {selectedCurrency.symbol}{amount}
                                </Button>
                              ))}
                            </div>
                          </div>
                        
                          {/* Custom Amount */}
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">{t('enterCustomAmount')}</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                                {selectedCurrency.symbol}
                              </span>
                              <Input
                                type="number"
                                placeholder={t('enterAmount')}
                                value={customAmount}
                                onChange={(e) => {
                                  setCustomAmount(e.target.value);
                                  setDonationAmount('');
                                }}
                                className="pl-8 bg-secondary border-border"
                              />
                            </div>
                          </div>
                          
                          {/* Tip for FundTheHope */}
                          <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Heart className="w-4 h-4 text-emerald-500" />
                                <span className="font-medium text-foreground text-sm">{t('tipFundTheHope')}</span>
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">
                              {t('helpKeepPlatformFree')}
                            </p>
                            {/* Tip Type Toggle */}
                            <div className="flex gap-2 mb-2">
                              <Button
                                type="button"
                                variant={!useCustomTip ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => {
                                  setUseCustomTip(false);
                                  setCustomTipAmount('');
                                }}
                                className={`flex-1 text-xs ${!useCustomTip ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-emerald-500/30'}`}
                              >
                                Percentage
                              </Button>
                              <Button
                                type="button"
                                variant={useCustomTip ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => {
                                  setUseCustomTip(true);
                                  setTipPercentage(0);
                                }}
                                className={`flex-1 text-xs ${useCustomTip ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-emerald-500/30'}`}
                              >
                                Custom Amount
                              </Button>
                            </div>
                            {/* Percentage Tips */}
                            {!useCustomTip && (
                              <div className="grid grid-cols-5 gap-1">
                                {[0, 10, 15, 20, 25].map((percent) => (
                                  <Button
                                    key={percent}
                                    type="button"
                                    variant={tipPercentage === percent ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setTipPercentage(percent)}
                                    className={`text-xs py-1 ${tipPercentage === percent ? 'bg-gradient-to-r from-emerald-500 to-teal-500 border-0' : 'border-emerald-500/30 hover:bg-emerald-500/10'}`}
                                  >
                                    {percent === 0 ? 'None' : `${percent}%`}
                                  </Button>
                                ))}
                              </div>
                            )}
                            {/* Custom Tip Amount */}
                            {useCustomTip && (
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                                  {selectedCurrency.symbol}
                                </span>
                                <Input
                                  type="number"
                                  placeholder="Enter tip amount"
                                  value={customTipAmount}
                                  onChange={(e) => setCustomTipAmount(e.target.value)}
                                  className="pl-8 h-9 bg-secondary border-emerald-500/30 text-sm"
                                />
                              </div>
                            )}
                            {/* Tip Display */}
                            {((!useCustomTip && tipPercentage > 0) || (useCustomTip && customTipAmount)) && (donationAmount || customAmount) && (
                              <div className="mt-2 pt-2 border-t border-border/50 flex justify-between text-sm">
                                <span className="text-muted-foreground">Your tip:</span>
                                <span className="font-medium text-emerald-400">
                                  {selectedCurrency.symbol}{calculateTipAmount().toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Right Column - Donor Info, Message, Summary */}
                        <div className="space-y-4">
                          {/* Guest Email - Only show if not logged in */}
                          {!user && (
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">
                                Email Address * <span className="text-muted-foreground font-normal">(for receipt)</span>
                              </label>
                              <Input
                                type="email"
                                placeholder="your@email.com"
                                value={guestEmail}
                                onChange={(e) => setGuestEmail(e.target.value)}
                                className="bg-secondary border-border"
                                required
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                {t('receiptEmail')}
                              </p>
                            </div>
                          )}
                          {/* Donor Info */}
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">{t('yourNameOptional')}</label>
                            <Input
                              type="text"
                              placeholder={t('enterYourName')}
                              value={donorName}
                              onChange={(e) => setDonorName(e.target.value)}
                              disabled={isAnonymous}
                              className="bg-secondary border-border"
                            />
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="anonymous"
                              checked={isAnonymous}
                              onChange={(e) => setIsAnonymous(e.target.checked)}
                              className="w-4 h-4 text-emerald-500 rounded border-border focus:ring-emerald-500"
                            />
                            <label htmlFor="anonymous" className="ml-2 text-sm text-muted-foreground">
                              {t('donateAnonymously')}
                            </label>
                          </div>
                          {/* Message */}
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">{t('leaveMessageOptional')}</label>
                            <Textarea
                              placeholder={t('writeMessage')}
                              value={donorMessage}
                              onChange={(e) => setDonorMessage(e.target.value)}
                              rows={2}
                              className="bg-secondary border-border"
                            />
                          </div>
                          
                          {/* Donation Summary */}
                          <div className="bg-secondary/50 border border-border rounded-xl p-4 space-y-2">
                            <h4 className="font-medium text-foreground mb-2">{t('donationSummary')}</h4>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{t('yourDonation')}:</span>
                              <span className="font-medium text-foreground">
                                {selectedCurrency.symbol}{parseFloat(donationAmount || customAmount || 0).toFixed(2)}
                              </span>
                            </div>
                            {calculateTipAmount() > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">
                                  {t('platformTip')} {!useCustomTip && tipPercentage > 0 ? `(${tipPercentage}%)` : ''}:
                                </span>
                                <span className="font-medium text-emerald-400">
                                  {selectedCurrency.symbol}{calculateTipAmount().toFixed(2)}
                                </span>
                              </div>
                            )}
                            <div className="border-t border-border pt-2 mt-2 flex justify-between">
                              <span className="font-semibold text-foreground">{t('total')}:</span>
                              <span className="font-bold text-lg text-emerald-400">
                                {selectedCurrency.symbol}{calculateTotalAmount().toFixed(2)}
                              </span>
                            </div>
                          </div>

                          {donationError && (
                            <p className="text-red-500 text-sm">{donationError}</p>
                          )}
                          
                          {/* Guest donation info */}
                          {!user && (
                            <p className="text-emerald-500 text-xs flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              {t('guestDonationInfo')}
                            </p>
                          )}
                          
                          <Button 
                            onClick={handleDonate} 
                            disabled={isSubmittingDonation || (!donationAmount && !customAmount) || (!user && !guestEmail)}
                            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 py-5"
                          >
                            {isSubmittingDonation ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                              </>
                            ) : (
                              <>
                                Pay with Card {(donationAmount || customAmount) ? `${selectedCurrency.symbol}${calculateTotalAmount().toFixed(2)}` : ''}
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* AI Share Button */}
                  <Button 
                    variant="outline" 
                    className="w-full py-5 rounded-xl border-emerald-500/50 hover:bg-emerald-500/10"
                    onClick={() => setShowSocialShare(true)}
                  >
                    <Sparkles className="mr-2 w-4 h-4 text-emerald-400" /> AI Social Share
                  </Button>

                  {/* Contact Creator Button */}
                  <Dialog open={showContactCreator} onOpenChange={setShowContactCreator}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full border-emerald-500/30 hover:bg-emerald-500/10"
                        data-testid="contact-creator-btn"
                      >
                        <MessageCircle className="mr-2 w-4 h-4 text-emerald-400" /> Contact Creator
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <MessageCircle className="w-5 h-5 text-emerald-400" />
                          Contact {campaign?.creator?.name || 'Creator'}
                        </DialogTitle>
                        <DialogDescription>
                          Send a message to the campaign creator. They will receive your message via email.
                        </DialogDescription>
                      </DialogHeader>
                      
                      {messageSent ? (
                        <div className="py-8 text-center">
                          <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400 mb-4" />
                          <p className="text-lg font-medium text-foreground">Message Sent!</p>
                          <p className="text-sm text-muted-foreground mt-2">
                            {campaign?.creator?.name || 'The creator'} will receive your message shortly.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4 mt-4">
                          <div>
                            <label className="text-sm font-medium text-foreground mb-1 block">Subject</label>
                            <Input
                              placeholder={`Question about: ${campaign?.title?.slice(0, 30)}...`}
                              value={contactSubject}
                              onChange={(e) => setContactSubject(e.target.value)}
                              className="bg-secondary"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-foreground mb-1 block">Your Message *</label>
                            <Textarea
                              placeholder="Write your message to the campaign creator..."
                              value={contactMessage}
                              onChange={(e) => setContactMessage(e.target.value)}
                              className="bg-secondary min-h-[120px]"
                              maxLength={1000}
                            />
                            <p className="text-xs text-muted-foreground mt-1 text-right">
                              {contactMessage.length}/1000
                            </p>
                          </div>
                          
                          {!user && (
                            <p className="text-xs text-amber-400 bg-amber-500/10 p-2 rounded">
                              💡 Sign in to include your contact information with the message.
                            </p>
                          )}
                          
                          <Button
                            onClick={handleSendMessageToCreator}
                            disabled={!contactMessage.trim() || sendingMessage}
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                          >
                            {sendingMessage ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                            ) : (
                              <><Mail className="w-4 h-4 mr-2" /> Send Message</>
                            )}
                          </Button>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>

                  {/* QR Code Payment */}
                  <QRPayment
                    campaignId={id}
                    campaignTitle={campaign.title}
                    suggestedAmount={50}
                  />

                  {/* Social Share - Quick Share Buttons */}
                  <TooltipProvider>
                    <div className="flex flex-wrap justify-center gap-2 mt-4">
                      {/* Facebook */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            onClick={() => {
                              const shareUrl = window.location.href;
                              const shareText = `Support ${campaign?.title || 'this campaign'} on FundTheHope!`;
                              // Use window.open for better compatibility
                              const fbUrl = `https://www.facebook.com/sharer.php?u=${encodeURIComponent(shareUrl)}`;
                              window.open(fbUrl, 'facebook-share', 'width=580,height=400,menubar=no,toolbar=no,resizable=yes,scrollbars=yes');
                            }}
                            className="p-2 bg-blue-500/20 rounded-full hover:bg-blue-500/30 transition-colors cursor-pointer inline-flex items-center justify-center"
                          >
                            <Facebook className="w-5 h-5 text-blue-400" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Share on Facebook</TooltipContent>
                      </Tooltip>

                      {/* Twitter/X */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a 
                            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Support ${campaign?.title || 'this campaign'} on FundTheHope!`)}&url=${encodeURIComponent(window.location.href)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-emerald-500/20 rounded-full hover:bg-emerald-500/30 transition-colors cursor-pointer inline-flex items-center justify-center"
                          >
                            <Twitter className="w-5 h-5 text-emerald-400" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>Share on X (Twitter)</TooltipContent>
                      </Tooltip>

                      {/* LinkedIn */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a 
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-blue-500/20 rounded-full hover:bg-blue-500/30 transition-colors cursor-pointer inline-flex items-center justify-center"
                          >
                            <Linkedin className="w-5 h-5 text-blue-400" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>Share on LinkedIn</TooltipContent>
                      </Tooltip>

                      {/* WhatsApp */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a 
                            href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`🌟 Support ${campaign?.title || 'this campaign'}\n\nHelp make a difference! ${window.location.href}`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-green-500/20 rounded-full hover:bg-green-500/30 transition-colors cursor-pointer inline-flex items-center justify-center"
                          >
                            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>Share on WhatsApp</TooltipContent>
                      </Tooltip>

                      {/* Email */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const subject = encodeURIComponent(`Support ${campaign?.title || 'this campaign'} on FundTheHope`);
                              const body = encodeURIComponent(`I wanted to share this campaign with you:\n\n${campaign?.title || 'Campaign'}\n\n${window.location.href}\n\nLet's help make a difference!`);
                              
                              // Open Gmail compose directly (most reliable cross-browser solution)
                              const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`;
                              window.open(gmailUrl, '_blank', 'width=600,height=600,menubar=no,toolbar=no,resizable=yes,scrollbars=yes');
                            }}
                            className="p-2 bg-secondary rounded-full hover:bg-secondary/80 transition-colors cursor-pointer inline-flex items-center justify-center"
                          >
                            <Mail className="w-5 h-5 text-muted-foreground" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Share via Email</TooltipContent>
                      </Tooltip>

                      {/* Copy Link */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            onClick={async (e) => {
                              e.preventDefault();
                              try {
                                const url = window.location.href;
                                await navigator.clipboard.writeText(url);
                                console.log('Link copied to clipboard');
                                alert('Link copied to clipboard!');
                              } catch (err) {
                                console.error('Copy failed:', err);
                                // Fallback method
                                const textArea = document.createElement('textarea');
                                textArea.value = window.location.href;
                                document.body.appendChild(textArea);
                                textArea.select();
                                try {
                                  document.execCommand('copy');
                                  alert('Link copied to clipboard!');
                                } catch (fallbackErr) {
                                  alert('Unable to copy link. Please copy manually: ' + window.location.href);
                                }
                                document.body.removeChild(textArea);
                              }
                            }}
                            className="p-2 bg-secondary rounded-full hover:bg-secondary/80 transition-colors cursor-pointer"
                          >
                            <Link2 className="w-5 h-5 text-muted-foreground" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Copy Link</TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                </div>

                {/* Team Fundraising CTA - Show if enabled */}
                {campaign.team_fundraising_enabled && (
                  <div className="bg-gradient-to-r from-violet-500/10 to-indigo-500/10 rounded-xl p-5 border border-violet-500/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-5 h-5 text-violet-400" />
                      <h4 className="font-semibold text-foreground">Fundraise as a Team</h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create your own page to support this campaign and share with your network.
                    </p>
                    <Link to={`/campaign/${id}/fundraise`}>
                      <Button className="w-full bg-violet-500 hover:bg-violet-600 text-white">
                        <UserPlus className="w-4 h-4 mr-2" /> Start Fundraising
                      </Button>
                    </Link>
                    {campaign.team_pages_count > 0 && (
                      <Link to={`/campaign/${id}/leaderboard`} className="block mt-3 text-center text-sm text-violet-400 hover:text-violet-300">
                        View {campaign.team_pages_count} Fundraiser{campaign.team_pages_count > 1 ? 's' : ''} →
                      </Link>
                    )}
                  </div>
                )}

                {/* Quick Actions for Campaign Owner */}
                {user && campaign.creator?.id === user?.id && (
                  <div className="bg-card rounded-xl p-5 border border-border">
                    <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-emerald-400" /> Campaign Manager
                    </h4>
                    
                    {/* Quick Actions */}
                    <div className="space-y-2 mb-4">
                      <Link to={`/campaign/${id}/edit`}>
                        <Button variant="outline" className="w-full justify-start border-border hover:bg-secondary">
                          <Edit className="w-4 h-4 mr-2 text-blue-400" />
                          Edit Campaign
                        </Button>
                      </Link>
                      <Link to="/verification">
                        <Button variant="outline" className="w-full justify-start border-border hover:bg-emerald-500/10">
                          <Wallet className="w-4 h-4 mr-2 text-emerald-400" />
                          Withdraw Funds
                        </Button>
                      </Link>
                      <Link to={`/campaign/${id}/update`}>
                        <Button variant="outline" className="w-full justify-start border-border hover:bg-teal-500/10">
                          <MessageCircle className="w-4 h-4 mr-2 text-teal-400" />
                          Post Update
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start border-border hover:bg-emerald-500/10"
                        onClick={() => {
                          const csvData = `Campaign Report\n\nTitle: ${campaign.title}\nRaised: ${campaign.raised}\nGoal: ${campaign.goal}\nDonors: ${campaign.donors}\nDays Left: ${campaign.daysLeft}\nProgress: ${Math.round(progressPercent)}%`;
                          const blob = new Blob([csvData], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${campaign.title.replace(/\s+/g, '-')}-report.csv`;
                          a.click();
                        }}
                      >
                        <Download className="w-4 h-4 mr-2 text-emerald-400" />
                        Export Reports
                      </Button>
                      <Link to="/verification">
                        <Button variant="outline" className="w-full justify-start border-border hover:bg-yellow-500/10">
                          <Shield className="w-4 h-4 mr-2 text-yellow-400" />
                          Verify Identity
                        </Button>
                      </Link>
                      
                      {/* Team Fundraising Toggle */}
                      <Button 
                        data-testid="team-fundraising-toggle-btn"
                        variant="outline" 
                        className={`w-full justify-start border-border ${campaign.team_fundraising_enabled ? 'hover:bg-red-500/10' : 'hover:bg-violet-500/10'}`}
                        disabled={actionLoading === 'team-fundraising'}
                        onClick={async () => {
                          if (actionLoading === 'team-fundraising') return;
                          setActionLoading('team-fundraising');
                          try {
                            const response = await apiClient.post(`/campaigns/${id}/team-fundraising/toggle`);
                            if (response.data) {
                              setCampaign(prev => ({
                                ...prev,
                                team_fundraising_enabled: response.data.team_fundraising_enabled
                              }));
                            }
                          } catch (err) {
                            console.error('Failed to toggle team fundraising:', err);
                            const status = err.response?.status;
                            const detail = err.response?.data?.detail;
                            if (status === 429) {
                              alert('Too many requests. Please wait a moment and try again.');
                            } else if (status === 401) {
                              alert('Session expired. Please login again.');
                            } else if (status === 403) {
                              alert('You are not authorized to modify this campaign.');
                            } else {
                              console.error('Toggle error:', { status, detail, error: err });
                              alert(detail || `Failed to toggle team fundraising (${status || 'network error'}). Please try again.`);
                            }
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'team-fundraising' ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Users className={`w-4 h-4 mr-2 ${campaign.team_fundraising_enabled ? 'text-violet-400' : 'text-muted-foreground'}`} />
                        )}
                        {campaign.team_fundraising_enabled ? 'Disable Team Fundraising' : 'Enable Team Fundraising'}
                      </Button>
                      
                      {/* Pause / Resume Campaign */}
                      {campaign.status === 'active' && (
                        <Button 
                          variant="outline" 
                          className="w-full justify-start border-border hover:bg-yellow-500/10 text-yellow-500"
                          onClick={handlePauseCampaign}
                          disabled={actionLoading === 'pause'}
                        >
                          {actionLoading === 'pause' ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Pause className="w-4 h-4 mr-2" />
                          )}
                          Pause Campaign
                        </Button>
                      )}
                      {campaign.status === 'inactive' && (
                        <Button 
                          variant="outline" 
                          className="w-full justify-start border-border hover:bg-green-500/10 text-green-500"
                          onClick={handleResumeCampaign}
                          disabled={actionLoading === 'resume'}
                        >
                          {actionLoading === 'resume' ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4 mr-2" />
                          )}
                          Resume Campaign
                        </Button>
                      )}
                      
                      {/* Delete Campaign */}
                      {campaign.status !== 'deleted' && (
                        <Button 
                          variant="outline" 
                          className="w-full justify-start border-border hover:bg-red-500/10 text-red-500"
                          onClick={handleDeleteCampaign}
                          disabled={actionLoading === 'delete'}
                        >
                          {actionLoading === 'delete' ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 mr-2" />
                          )}
                          Delete Campaign
                        </Button>
                      )}
                    </div>
                    
                    {/* Donation History & Notifications */}
                    <div className="border-t border-border pt-4 space-y-2">
                      <Button 
                        variant="outline" 
                        className="w-full justify-start border-border hover:bg-secondary"
                        onClick={() => setShowDonationHistory(true)}
                      >
                        <History className="w-4 h-4 mr-2 text-orange-400" />
                        Donation History
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start border-border hover:bg-secondary"
                        onClick={() => {
                          setShowNotifications(true);
                          fetchNotifications();
                        }}
                      >
                        <Bell className="w-4 h-4 mr-2 text-emerald-400" />
                        Notifications
                      </Button>
                      
                      {/* Generate Referral Link Button — hidden when REFERRALS_ENABLED=false */}
                      {REFERRALS_ENABLED && (
                        <Button 
                          variant="outline" 
                          className="w-full justify-start border-border hover:bg-violet-500/10"
                          onClick={handleGenerateReferralCode}
                          disabled={referralLoading}
                          data-testid="generate-referral-btn"
                        >
                          {referralLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Share2 className="w-4 h-4 mr-2 text-violet-400" />
                          )}
                          Generate Referral Link
                        </Button>
                      )}
                    </div>
                    
                    {/* AI Tools Section */}
                    <div className="border-t border-border pt-4 mt-4">
                      <h5 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
                        <Sparkles className="w-4 h-4 text-emerald-400" /> AI Tools
                      </h5>
                      <div className="space-y-2">
                        <Button
                          variant="outline"
                          className="w-full justify-start border-emerald-500/30 hover:bg-emerald-500/10"
                          onClick={() => setShowOptimizer(true)}
                        >
                          <TrendingUp className="w-4 h-4 mr-2 text-emerald-400" />
                          Campaign Optimizer
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full justify-start border-emerald-500/30 hover:bg-emerald-500/10"
                          onClick={() => setShowThankYou(true)}
                        >
                          <Heart className="w-4 h-4 mr-2 text-rose-400" />
                          Thank You Messages
                        </Button>
                        
                        {/* Auto Thank You Toggle */}
                        <div className="flex items-center justify-between p-3 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
                          <div className="flex items-center gap-2">
                            <Heart className="w-4 h-4 text-rose-400" />
                            <span className="text-sm text-foreground">Auto Thank You</span>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const newValue = !autoThankYouEnabled;
                                const response = await apiClient.post(`/donations/campaign/${id}/auto-thank-you`, {
                                  enabled: newValue
                                });
                                if (response.data.success) {
                                  setAutoThankYouEnabled(newValue);
                                }
                              } catch (err) {
                                console.error('Failed to toggle auto thank you:', err);
                              }
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              autoThankYouEnabled ? 'bg-emerald-500' : 'bg-gray-600'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                autoThankYouEnabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Live Donors Feed - Scrolling Up Animation */}
                <div className="bg-card rounded-xl p-5 border border-border overflow-hidden">
                  <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Heart className="w-5 h-5 text-emerald-500 animate-pulse" />
                    Recent Supporters
                    <span className="ml-auto text-xs text-muted-foreground font-normal">Live</span>
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                  </h4>
                  <div className="relative h-[320px] overflow-hidden">
                    <div className="animate-scroll-up space-y-2">
                      {/* Show 6 donors visible at a time with scrolling */}
                      {[...donations, ...donations].slice(0, 24).map((donation, index) => (
                        <div 
                          key={`${donation.id}-${index}`}
                          className="flex items-center gap-3 p-2.5 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
                        >
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                            <Heart className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {donation.donor}
                            </p>
                            <p className="text-xs text-muted-foreground">{donation.time}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-emerald-400">
                              {formatCurrency(donation.amount)}
                            </p>
                            {donation.message && (
                              <p className="text-xs text-muted-foreground truncate max-w-[80px]">
                                {donation.message}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Gradient fade at top and bottom */}
                    <div className="absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-card to-transparent pointer-events-none z-10"></div>
                    <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-card to-transparent pointer-events-none z-10"></div>
                  </div>
                  {donations.length === 0 && (
                    <p className="text-center text-muted-foreground text-sm py-4">
                      Be the first to support this campaign!
                    </p>
                  )}
                </div>

                {/* AI Success Predictor */}
                <SuccessPredictor campaignId={id} />

                {/* AI Trust Score */}
                <TrustScoreCard campaignId={id} />

                {/* Report */}
                <button 
                  onClick={() => setShowReportDialog(true)}
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground flex items-center justify-center"
                  data-testid="report-campaign-btn"
                >
                  <Flag className="w-4 h-4 mr-1" /> Report this campaign
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {/* AI Feature Modals */}
      <SocialShareModal
        isOpen={showSocialShare}
        onClose={() => setShowSocialShare(false)}
        campaignId={id}
        campaignTitle={campaign?.title}
        campaignUrl={window.location.href}
      />

      <CampaignOptimizerModal
        isOpen={showOptimizer}
        onClose={() => setShowOptimizer(false)}
        campaignId={id}
      />

      <ThankYouGenerator
        isOpen={showThankYou}
        onClose={() => setShowThankYou(false)}
        campaignId={id}
        donors={donations}
      />

      {/* Donation History Modal */}
      <Dialog open={showDonationHistory} onOpenChange={setShowDonationHistory}>
        <DialogContent className="max-w-2xl bg-card border-border max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-foreground text-xl flex items-center gap-2">
              <History className="w-5 h-5 text-orange-400" />
              Donation History
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Complete list of all donations received for "{campaign?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4 space-y-3">
            {donations.length > 0 ? (
              donations.map((donation, index) => (
                <div 
                  key={donation.id || index} 
                  className="flex items-start gap-4 p-4 bg-secondary/50 rounded-lg border border-border"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-foreground truncate">{donation.donor}</p>
                      <span className="font-bold text-emerald-400 whitespace-nowrap">
                        {formatCurrency(donation.amount)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{donation.time}</p>
                    {donation.message && (
                      <p className="text-sm text-muted-foreground mt-2 italic bg-background/50 p-2 rounded">
                        "{donation.message}"
                      </p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No donations yet</p>
                <p className="text-sm text-muted-foreground mt-1">Donations will appear here once received</p>
              </div>
            )}
          </div>
          <div className="border-t border-border pt-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{donations.length}</span> total donations
            </div>
            <Button variant="outline" onClick={() => setShowDonationHistory(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notifications Modal */}
      <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
        <DialogContent className="max-w-lg bg-card border-border max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-foreground text-xl flex items-center gap-2">
              <Bell className="w-5 h-5 text-emerald-400" />
              Notifications
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Stay updated with your campaign activity
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4 space-y-2">
            {notificationsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
              </div>
            ) : notifications.length > 0 ? (
              notifications.map((notification) => (
                <div 
                  key={notification.id}
                  onClick={() => !notification.read && markNotificationRead(notification.id)}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    notification.read 
                      ? 'bg-secondary/30 border-border' 
                      : 'bg-emerald-500/10 border-emerald-500/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                      notification.read ? 'bg-muted-foreground' : 'bg-emerald-400'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${notification.read ? 'text-muted-foreground' : 'text-foreground'}`}>
                        {notification.title}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(notification.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No notifications yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  You'll be notified when someone donates or comments
                </p>
              </div>
            )}
          </div>
          <div className="border-t border-border pt-4 flex items-center justify-between">
            {notifications.filter(n => !n.read).length > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={markAllNotificationsRead}
                className="text-emerald-400 hover:text-emerald-300"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Mark all as read
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowNotifications(false)} className="ml-auto">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Campaign Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground text-xl flex items-center gap-2">
              <Flag className="w-5 h-5 text-red-400" />
              Report Campaign
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Help us maintain a safe community by reporting suspicious or inappropriate campaigns.
            </DialogDescription>
          </DialogHeader>
          
          {reportSubmitted ? (
            <div className="py-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground">Report Submitted</h3>
              <p className="text-muted-foreground mt-2">
                Thank you for helping keep our community safe. We'll review this report within 24-48 hours.
              </p>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Reason for Report <span className="text-red-400">*</span>
                </label>
                <select
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="report-reason-select"
                >
                  <option value="">Select a reason...</option>
                  <option value="fraud">Suspected Fraud or Scam</option>
                  <option value="misleading">Misleading Information</option>
                  <option value="inappropriate">Inappropriate Content</option>
                  <option value="spam">Spam or Repetitive Campaign</option>
                  <option value="impersonation">Impersonation</option>
                  <option value="illegal">Illegal Activity</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Additional Details
                </label>
                <textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  placeholder="Please provide any additional information that might help us investigate..."
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[100px] resize-none"
                  data-testid="report-details-input"
                />
              </div>
              
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-sm text-amber-200">
                  <strong>Note:</strong> Filing false reports may result in account restrictions. 
                  Please only report campaigns that genuinely violate our community guidelines.
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowReportDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReportCampaign}
                  disabled={isSubmittingReport || !reportReason}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="submit-report-btn"
                >
                  {isSubmittingReport ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><Flag className="w-4 h-4 mr-2" /> Submit Report</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Referral Code Modal — hidden when REFERRALS_ENABLED=false */}
      {REFERRALS_ENABLED && (
        <Dialog open={showReferralModal} onOpenChange={setShowReferralModal}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground text-xl flex items-center gap-2">
              <Share2 className="w-5 h-5 text-violet-400" />
              Your Referral Link
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Share this unique link to track referrals and earn rewards when people donate through your link.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Referral Code Display */}
            <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4">
              <label className="text-xs text-muted-foreground block mb-2">Your Referral Code</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-lg font-mono font-bold text-violet-400" data-testid="referral-code-display">
                  {generatedReferralCode}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(generatedReferralCode);
                      alert('Referral code copied!');
                    } catch (err) {
                      console.error('Failed to copy:', err);
                    }
                  }}
                  data-testid="copy-referral-code-btn"
                >
                  <Link2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Full Referral Link */}
            <div className="bg-secondary rounded-lg p-4">
              <label className="text-xs text-muted-foreground block mb-2">Full Referral Link</label>
              <div className="flex items-center gap-2">
                <input 
                  type="text"
                  readOnly
                  value={`${window.location.origin}/campaign/${campaign?.id}?ref=${generatedReferralCode}`}
                  className="flex-1 text-sm bg-background border border-border rounded px-2 py-1 text-foreground"
                  data-testid="referral-link-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const url = `${window.location.origin}/campaign/${campaign?.id}?ref=${generatedReferralCode}`;
                      await navigator.clipboard.writeText(url);
                      alert('Referral link copied!');
                    } catch (err) {
                      console.error('Failed to copy:', err);
                    }
                  }}
                  data-testid="copy-referral-link-btn"
                >
                  <Link2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Share Buttons */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Share on Social Media</label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    const url = `${window.location.origin}/campaign/${campaign?.id}?ref=${generatedReferralCode}`;
                    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(`Support this campaign: ${campaign?.title}`)}`, '_blank');
                  }}
                >
                  <Twitter className="w-4 h-4 mr-1" /> Twitter
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    const url = `${window.location.origin}/campaign/${campaign?.id}?ref=${generatedReferralCode}`;
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                  }}
                >
                  <Facebook className="w-4 h-4 mr-1" /> Facebook
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    const url = `${window.location.origin}/campaign/${campaign?.id}?ref=${generatedReferralCode}`;
                    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
                  }}
                >
                  <Linkedin className="w-4 h-4 mr-1" /> LinkedIn
                </Button>
              </div>
            </div>
            
            {/* Info Box */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
              <p className="text-sm text-emerald-300">
                <strong>Tip:</strong> When someone donates using your referral link, you'll earn rewards. 
                Track your referral stats in the "Invite & Earn" section of your dashboard.
              </p>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowReferralModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
};

export default CampaignDetailPage;
